import { Alert, Button, Form, Modal, type ModalProps, theme } from 'antd';
import { type RequiredMark } from 'antd/lib/form/Form';
import React, { type PropsWithChildren, useEffect, useState } from 'react';
import styled from 'styled-components';

import { extractUserMessage } from 'utils/FormUtils';

export type SubmitCallback = (values: Record<string, any>) => Promise<void>;

const DEFAULT_OK_TEXT = 'Submit';
const DEFAULT_CANCEL_TEXT = 'Cancel';

const { colorPrimary } = theme.getDesignToken();

const ModalFormRequiredMark = styled.span`
  color: ${colorPrimary};
`;

const renderRequiredMark: RequiredMark = (labelNode, info) => {
  if (!info.required) return labelNode;

  return (
    <>
      <ModalFormRequiredMark title="Required">•&nbsp;</ModalFormRequiredMark>
      {labelNode}
    </>
  );
};

export type ModalFormProps = PropsWithChildren<{
  cancelText?: string;
  formName: string;
  modalStyles?: ModalProps['styles'];
  modalWidth?: string | number;
  okText?: string;
  onClose?: () => void;
  onSubmit?: SubmitCallback;
  open?: boolean;
  takeFocusWhenOpening?: boolean;
  title?: React.ReactNode;
}>;

export function ModalForm({
  cancelText,
  children,
  formName,
  modalStyles,
  modalWidth,
  okText,
  onClose,
  onSubmit,
  open,
  takeFocusWhenOpening,
  title,
}: ModalFormProps) {
  if (takeFocusWhenOpening == null) takeFocusWhenOpening = true;

  const [isBusy, setIsBusy] = useState(false);
  const [errorDescription, setErrorDescription] = useState('');
  const [formController] = Form.useForm();

  const handleFormFinish = async (formValues: Record<string, any>) => {
    setIsBusy(true);
    setErrorDescription('');
    try {
      if (onSubmit) await onSubmit(formValues);
      onClose?.();
    } catch (submitError) {
      setErrorDescription(extractUserMessage(submitError));
      console.error(submitError);
    } finally {
      setIsBusy(false);
    }
  };

  const handleOk = () => {
    formController.submit();
  };

  const handleCancel = () => onClose?.();

  useEffect(() => {
    // Set keyboard focus to the first form input when appearing.
    // Seems this has to be done asynchronously, hence the rAF
    if (open && takeFocusWhenOpening) {
      requestAnimationFrame(() => {
        const formEl = formName
          ? window.document.getElementById(formName)
          : null;
        if (formEl?.tagName === 'FORM') {
          const firstInput = formEl.querySelector('input');
          if (firstInput instanceof HTMLElement) {
            firstInput.focus();
          }
        }
      });
      setErrorDescription('');
    }
  }, [formName, open, takeFocusWhenOpening]);

  const footer = (
    <>
      <Button key="cancel" disabled={isBusy} onClick={handleCancel}>
        {cancelText || DEFAULT_CANCEL_TEXT}
      </Button>
      <Button key="ok" loading={isBusy} onClick={handleOk} type="primary">
        {okText || DEFAULT_OK_TEXT}
      </Button>
    </>
  );

  // even though we're replacing the prefab footer buttons, we still need
  // `onCancel` to handle the close button in the modal's top right
  return (
    <Modal
      destroyOnHidden={true}
      footer={footer}
      onCancel={handleCancel}
      open={open}
      styles={modalStyles}
      title={title}
      width={modalWidth}
    >
      <Form
        preserve={false}
        form={formController}
        name={formName}
        disabled={isBusy}
        onFinish={handleFormFinish}
        requiredMark={renderRequiredMark}
      >
        {children}

        {errorDescription && (
          <Alert
            message="An error occurred"
            description={errorDescription}
            type="error"
            showIcon
            closable
          />
        )}
      </Form>
    </Modal>
  );
}
